import { bind as bound } from 'bind-decorator';
import { RadioButton, RadioButtonGroup } from 'bz-core-controls';
import type { IBaseFieldProps } from 'bz-core-controls/controls/form/field/generics/IBaseFieldProps';
import ColourPicker from 'bz-core-controls/controls/general/colour/colour-picker';
import Checkbox from 'bz-core-controls/controls/next/form/checkbox';
import type { ComponentType } from 'react';
import { CurrencyLabel, DateLabel, Label, LabelGroup } from '../../label';
import Field from '../field';
import type { IFieldProps } from '../field/IFieldProps';
import FieldCombobox from '../field-combobox';
import FieldCurrency from '../field-currency';
import FieldDatePicker from '../field-date-picker';
import FieldDateRangePicker from '../field-date-range-picker/';
import FieldInteger from '../field-integer';
import FieldDecimal from '../field-integer';
import FieldMultiSelect from '../field-multi-select';
import type { FullProps } from '../field-select';
import FieldSelect from '../field-select';
import FieldTimePicker from '../field-time-picker';
import TimePicker from '../time-picker';
import type { GenericProps } from './GenerateComponentType';
import { GenerateComponentType } from './GenerateComponentType';

const components: { [key: string]: any } = {};

export class Fields<T> {
  getCached<P>(
    key: string,
    RawComponent: ComponentType<P>,
    onChangeMethodName: string = 'onChange'
  ): ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>> {
    let res = components[key] as ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>>;
    if (!res) {
      components[key] = GenerateComponentType<T, P>(RawComponent, onChangeMethodName);
      res = components[key] as ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>>;
    }
    return res;
  }
  get Field() {
    return this.getCached<IFieldProps>('Field', Field);
  }
  get FieldSelect() {
    return this.getCached<FullProps>('FieldSelect', FieldSelect);
  }
  get FieldCombobox() {
    return this.getCached<FullProps>('FieldCombobox', FieldCombobox);
  }
  get FieldMultiSelect() {
    return this.getCached<FullProps>('FieldMultiSelect', FieldMultiSelect);
  }
  get FieldDatePicker() {
    return this.getCached<IFieldProps>('FieldDatePicker', FieldDatePicker);
  }
  get FieldDateRangePicker() {
    return this.getCached<IFieldProps>('FieldDateRangePicker', FieldDateRangePicker);
  }
  get FieldTimePicker() {
    return this.getCached<IFieldProps>('FieldTimePicker', TimePicker);
  }
  get FieldInteger() {
    return this.getCached<IFieldProps>('FieldInteger', FieldInteger);
  }
  get FieldDecimal() {
    return this.getCached<IFieldProps>('FieldDecimal', FieldDecimal);
  }
  get FieldCurrency() {
    return this.getCached<IFieldProps>('FieldCurrency', FieldCurrency);
  }
  get Checkbox() {
    return this.getCached<IFieldProps>('Checkbox', Checkbox, 'onCheckboxChange');
  }
  get RadioButtonGroup() {
    return this.getCached<IFieldProps>('RadioButtonGroup', RadioButtonGroup);
  }
  get DateLabel() {
    return this.getCached<any>('DateLabel', DateLabel);
  }
  get Label() {
    return this.getCached<any>('Label', Label);
  }
  get CurrencyLabel() {
    return this.getCached<any>('CurrencyLabel', CurrencyLabel);
  }
  get LabelGroup() {
    return LabelGroup;
  }
  get RadioButton() {
    return RadioButton;
  }
  get ColourPicker() {
    return this.getCached<IFieldProps>('ColourPicker', ColourPicker);
  }

  @bound
  create<P>(
    key: string,
    component: ComponentType<P>
  ): ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>> {
    return this.getCached<P>(key, component);
  }
}
